import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useClientCheckinOverviewQuery } from "@src/queries/clients";
import { ResponseVitalityCheckinVitalityCheckinModuleScoreResponse } from "@src/api/v1/data-contracts";
import { formatCheckinDate } from "@src/utils/formatter";
import { TypographyWithIcon } from "./utils/TypographyWithIcon";
import ClientCheckinsEmpty from "./ClientCheckinsEmpty";
import ClientCheckinsOverviewTable from "./ClientCheckinsOverviewTable";
import TableHeader, { Column } from "./table/TableHeader";

interface ClientCheckinsOverviewProps {
  clientId: string;
}

const columns: Column[] = [
  {
    name: "",
    minWidth: 70,
  },
  {
    name: "Checkins.Theme",
    minWidth: 250,
  },
  {
    name: "Checkins.Score",
    minWidth: 130,
  },
  {
    name: "Checkins.Date",
    minWidth: 200,
  },
  {
    name: "Checkins.Change",
    minWidth: 100,
  },
];

const ClientCheckinsOverview: FC<ClientCheckinsOverviewProps> = ({
  clientId,
}: ClientCheckinsOverviewProps) => {
  const { data: checkins } = useClientCheckinOverviewQuery(clientId);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [openFocus, setOpenFocus] = useState(true);
  const [openOthers, setOpenOthers] = useState(true);
  const [openAll, setOpenAll] = useState(true);

  if (!checkins) return null;

  const focusedThemes: ResponseVitalityCheckinVitalityCheckinModuleScoreResponse[] =
    checkins?.filter((checkin) => checkin.isFocusedTheme === true);

  const unfocusedThemes: ResponseVitalityCheckinVitalityCheckinModuleScoreResponse[] =
    checkins?.filter((checkin) => checkin.isFocusedTheme === false);

  const dataCheckins: ResponseVitalityCheckinVitalityCheckinModuleScoreResponse[] =
    checkins?.filter((checkin) => checkin.key !== null);

  const isAllDataPresent = focusedThemes.length === 0;

  const themeInformation = (
    checkin: ResponseVitalityCheckinVitalityCheckinModuleScoreResponse,
  ) => {
    const redirectRoute = checkin.themeKey
      ? checkin.themeKey.toLowerCase()
      : "";

    navigate(`${redirectRoute}`);
  };

  return (
    <div className="rounded-lg bg-white shadow-md">
      <div className="p-6">
        <TypographyWithIcon variant="h5" iconLigature="fact_check" outlined>
          {t("Common.Checkin")}
        </TypographyWithIcon>

        {dataCheckins?.length > 0 ? (
          <div className="overflow-x-auto">
            <table
              className="min-w-[650px] w-full mt-8 border-collapse"
              aria-label="simple table"
            >
              <TableHeader columns={columns} />
              <tbody>
                {isAllDataPresent ? (
                  checkins.map((theme, index) => (
                    <ClientCheckinsOverviewTable
                      key={theme.themeKey}
                      question={theme}
                      message={t("Checkins.All")}
                      getCorrectDate={formatCheckinDate}
                      index={index}
                      open={openAll}
                      setOpen={setOpenAll}
                      onClick={
                        theme.key ? () => themeInformation(theme) : undefined
                      }
                      hideArrow={checkins.length > 1}
                    />
                  ))
                ) : (
                  <>
                    {focusedThemes?.map((theme, index) => (
                      <ClientCheckinsOverviewTable
                        key={theme.themeKey}
                        question={theme}
                        message={t("Checkins.Focus")}
                        getCorrectDate={formatCheckinDate}
                        index={index}
                        open={openFocus}
                        setOpen={setOpenFocus}
                        onClick={
                          theme.key ? () => themeInformation(theme) : undefined
                        }
                        hideArrow={focusedThemes.length > 1}
                      />
                    ))}
                    {unfocusedThemes?.map((theme, index) => (
                      <ClientCheckinsOverviewTable
                        key={theme.themeKey}
                        question={theme}
                        message={t("Checkins.Others")}
                        getCorrectDate={formatCheckinDate}
                        index={index}
                        open={openOthers}
                        setOpen={setOpenOthers}
                        onClick={
                          theme.key ? () => themeInformation(theme) : undefined
                        }
                        hideArrow={unfocusedThemes.length > 1}
                      />
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <ClientCheckinsEmpty />
        )}
      </div>
    </div>
  );
};

export default ClientCheckinsOverview;
